import { 
   CheckCircle, 
   XCircle,
   AlertTriangle // Replacing QuestionMarkCircleIcon
} from 'lucide-react';
import type { Verdict } from '../../types/models';
import { cn } from "@/lib/utils";

interface VerdictCardProps {
  verdict: Verdict;
  reasoning: string[];
}

// Define styles based on semantic meaning / theme colors
const verdictStyles = {
  STRONG_FIT: {
    icon: CheckCircle,
    iconColor: 'text-green-600', // Or use text-primary based on theme
    title: 'Strong Fit',
    titleColor: 'text-green-700',
    textColor: 'text-green-800 dark:text-green-300',
  },
  POTENTIAL_FIT: {
    icon: AlertTriangle, // More indicative than HelpCircle?
    iconColor: 'text-yellow-600',
    title: 'Potential Fit',
    titleColor: 'text-yellow-700',
    textColor: 'text-yellow-800 dark:text-yellow-300',
  },
  NEEDS_CONSIDERATION: {
    icon: AlertTriangle,
    iconColor: 'text-yellow-600',
    title: 'Needs Consideration',
    titleColor: 'text-yellow-700',
    textColor: 'text-yellow-800 dark:text-yellow-300',
  },
  NOT_RECOMMENDED: {
    icon: XCircle,
    iconColor: 'text-destructive', // Use theme's destructive color
    title: 'Not Recommended',
    titleColor: 'text-destructive',
    textColor: 'text-destructive/90',
  }
} as const;

export function VerdictCard({ verdict, reasoning }: VerdictCardProps) {
   // Handle potential unexpected verdict values gracefully
  const safeVerdict = verdict in verdictStyles ? verdict : 'NEEDS_CONSIDERATION';
  const style = verdictStyles[safeVerdict];
  const Icon = style.icon;
  
  return (
    // Removed outer div - parent Card provides container
    <div className="space-y-3">
       {/* Verdict Title and Icon */}
      <div className="flex items-center space-x-2">
        <Icon className={cn("h-5 w-5 flex-shrink-0", style.iconColor)} />
        <h4 className={cn("font-semibold", style.titleColor)}>
          {style.title}
        </h4>
      </div>
      
       {/* Reasoning List - Simplified styling */}
      <ul className="space-y-1.5 pl-5 text-sm list-disc list-outside">
        {reasoning.map((reason, index) => (
          <li key={index} className={cn(style.textColor)}>
            {reason}
          </li>
        ))}
      </ul>
    </div>
  );
}